import { IMAGES } from "./constants";

export interface BlogAuthor {
  name: string;
  role: string;
  description: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  dateISO: string;
  lastUpdated?: string;
  lastUpdatedISO?: string;
  image: string;
  ogImage?: string;
  imageAlt: string;
  category: string;
  readTime: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  author: BlogAuthor;
  factCheckedBy: BlogAuthor;
  keyTakeaways: string[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: "lissmac-gradningsmaskin",
    title: "Lissmac gradningsmaskin – ASPAB investerar i ännu högre kvalitet",
    excerpt:
      "ASPAB utökar maskinparken med en avancerad Lissmac gradningsmaskin. Investeringen stärker vår kapacitet inom avgradning, kantbrytning och ytfinish – och innebär att laserskurna plåtar levereras helt utan vassa kanter.",
    date: "12 mars 2026",
    dateISO: "2026-03-12",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.lissmacMaskin,
    ogImage: "/images/og-lissmac-gradningsmaskin_3f4bc689.png",
    imageAlt: "Lissmac SMD 123 gradningsmaskin hos ASPAB i Eskilstuna",
    category: "Investering",
    readTime: "3 min",
    metaTitle: "Lissmac gradningsmaskin – ASPAB investerar i högre kvalitet | ASPAB",
    metaDescription:
      "ASPAB investerar i en Lissmac gradningsmaskin för automatisk avgradning och kantbrytning av laserskuren plåt. Inga vassa kanter, bättre ytfinish och säkrare hantering.",
    keywords:
      "Lissmac, gradningsmaskin, avgradning, kantbrytning, laserskärning, plåtbearbetning, ASPAB, ytfinish, Eskilstuna",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning med praktisk erfarenhet av laserskärning och avgradning.",
    },
    keyTakeaways: [
      "ASPAB har investerat i en Lissmac SBM-L 1500 G1S2 gradningsmaskin för automatisk avgradning av laserskuren plåt.",
      "Maskinen bearbetar båda sidor av plåten i ett enda genomlopp, utan att arbetsstycket behöver vändas.",
      "Avgradning eliminerar vassa kanter och ger en jämn, säker ytfinish som förbättrar passform vid bockning och svetsning.",
      "Automatiserad avgradning ersätter manuellt sliparbete och kortar ledtiderna avsevärt vid större serier.",
      "Lissmac-maskinen hanterar svart stål, rostfritt, aluminium och höghållfasthetsstål.",
    ],
  },
  {
    slug: "laserskarning-vs-plasmaskaring",
    title: "Laserskärning vs plasmaskärning – vilken metod passar ditt projekt?",
    excerpt:
      "En jämförelse av laserskärning och plasmaskärning med fokus på precision, materialtjocklek, kostnadseffektivitet och ytfinish. Lär dig vilken metod som passar bäst för ditt projekt.",
    date: "28 mars 2026",
    dateISO: "2026-03-28",
    lastUpdated: "23 september 2026",
    lastUpdatedISO: "2026-09-23",
    image: IMAGES.heroLaser,
    ogImage: "/images/og-laserskarning-vs-plasmaskaring_df52945c.png",
    imageAlt: "Laserskärning av stålplåt hos ASPAB i Eskilstuna",
    category: "Kunskap",
    readTime: "5 min",
    metaTitle: "Laserskärning vs plasmaskärning – jämförelse av precision, kostnad och material | ASPAB",
    metaDescription:
      "Jämförelse av laserskärning och plasmaskärning: precision, materialtjocklek, kostnad och ytfinish. ASPAB förklarar vilken metod som passar ditt projekt.",
    keywords:
      "laserskärning vs plasmaskärning, jämförelse skärmetoder, precision laserskärning, plasmaskärning, plåtskärning, metallbearbetning",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning ger toleranser ner till 0,1 mm, medan plasmaskärning normalt håller 0,5–1,5 mm.",
      "Plasmaskärning är ofta billigare vid tjockt material (över 25 mm), men laserskärning är mer kostnadseffektiv vid tunnare plåt.",
      "Laserskärning ger en renare skäryta som sällan kräver efterbearbetning.",
      "Laserskärning ger hög precision i stål, rostfritt och aluminium, medan plasmaskärning är begränsad till elektriskt ledande material.",
      "Valet beror på materialtjocklek, toleranskrav, seriens storlek och budget.",
    ],
  },
  {
    slug: "materialguide-stal-rostfritt-aluminium",
    title: "Materialguide: stål, rostfritt och aluminium vid laserskärning",
    excerpt:
      "En praktisk guide till de vanligaste materialen vid laserskärning. Vi går igenom egenskaper, tjockleksgränser och typiska användningsområden för svart stål, rostfritt stål och aluminium.",
    date: "2 april 2026",
    dateISO: "2026-04-02",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.detail,
    ogImage: "/images/og-materialguide-stal-rostfritt-aluminium_508e7656.png",
    imageAlt: "Närbild på laserskuren stålplåt hos ASPAB",
    category: "Kunskap",
    readTime: "6 min",
    metaTitle: "Materialguide: stål, rostfritt och aluminium vid laserskärning | ASPAB",
    metaDescription:
      "Guide till material vid laserskärning: svart stål (upp till 25 mm), rostfritt stål (upp till 20 mm) och aluminium. Egenskaper, gränser och användningsområden.",
    keywords:
      "materialguide laserskärning, svart stål laserskärning, rostfritt stål laserskärning, aluminium laserskärning, materialtjocklek, plåtmaterial",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Svart stål (S235, S355) är det vanligaste materialet vid laserskärning och kan skäras upp till 25 mm tjocklek.",
      "Rostfritt stål (304, 316) ger korrosionsbeständighet och kan skäras upp till 20 mm i ASPAB:s CO2-lasrar.",
      "Aluminium kräver högre effekt på grund av reflektivitet men ger utmärkt vikt-till-styrka-förhållande.",
      "Höghållfasthetsstål som Strenx och Hardox används inom försvar, jordbruk och tunga fordon.",
      "Materialvalet påverkar skärhastighet, tolerans, efterbearbetning och totalkostnad.",
    ],
  },
  {
    slug: "sa-forbereder-du-cad-filer-for-laserskarning",
    title: "Så förbereder du CAD-filer för laserskärning – tips från ASPAB",
    excerpt:
      "Praktiska tips för att förbereda CAD-filer för laserskärning. Rätt filformat, toleranser, materialval och vanliga misstag att undvika för snabbare leverans och bättre resultat.",
    date: "5 april 2026",
    dateISO: "2026-04-05",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.detail,
    ogImage: "/images/og-sa-forbereder-du-cad-filer-for-laserskarning_be65fc43.png",
    imageAlt: "CNC-styrd laserskärmaskin hos ASPAB i Eskilstuna",
    category: "Tips",
    readTime: "4 min",
    metaTitle: "Så förbereder du CAD-filer för laserskärning – praktiska tips | ASPAB",
    metaDescription:
      "Tips för att förbereda CAD-filer för laserskärning: filformat, toleranser, materialval och vanliga misstag. Snabbare leverans och bättre resultat med rätt förberedelse.",
    keywords:
      "CAD-filer laserskärning, DXF laserskärning, STEP laserskärning, filformat plåtbearbetning, förbereda ritningar, laserskärning tips",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "DXF och STEP är de vanligaste filformaten för laserskärning – undvik PDF och bildfiler.",
      "Ange alltid materialtjocklek, kvalitet och antal i beställningen för att undvika fördröjningar.",
      "Minsta håldiameter bör vara minst lika stor som materialtjockleken för bästa resultat.",
      "Undvik skarpa innerhörn (under 90 grader) – använd radier för att minska sprickrisken.",
      "Skicka gärna en enkel skiss om du saknar CAD – ASPAB hjälper till med konstruktion.",
    ],
  },
  {
    slug: "laserskarning-tjocklek-guide",
    title: "Laserskärning och materialtjocklek – vad kan man skära?",
    excerpt: "Hur tjockt material kan laserskärning hantera? Utforska de maximala tjocklekarna för stål, rostfritt och aluminium och lär dig hur tjockleken påverkar precision, hastighet och kostnad.",
    date: "8 april 2026",
    dateISO: "2026-04-08",
    lastUpdated: "23 september 2026",
    lastUpdatedISO: "2026-09-23",
    image: IMAGES.closeup1,
    ogImage: "/images/og-laserskarning-tjocklek-guide_99361d01.png",
    imageAlt: "Laserskärning av tjock stålplåt hos ASPAB i Eskilstuna",
    category: "Kunskap",
    readTime: "7 min",
    metaTitle: "Laserskärning och materialtjocklek – vad kan man skära? | ASPAB",
    metaDescription: "En komplett guide till laserskärning och materialtjocklek. Lär dig max tjocklek för stål (25mm), rostfritt (20mm) och aluminium (15mm) hos ASPAB.",
    keywords: "laserskarning, tjocklek, guide",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-rostfritt-stal",
    title: "Laserskärning i rostfritt stål – utmaningar och lösningar",
    excerpt: "Att uppnå perfekta, oxidfria snitt i rostfritt stål kräver djup kunskap. Upptäck hur ASPAB bemästrar utmaningar som reflektivitet och värmeledning för att leverera precisionskomponenter.",
    date: "9 april 2026",
    dateISO: "2026-04-09",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.laserOldSite,
    ogImage: "/images/og-laserskarning-rostfritt-stal_1361454b.png",
    imageAlt: "Närbild på laserskuret rostfritt stål",
    category: "Kunskap",
    readTime: "8 min",
    metaTitle: "Laserskärning i rostfritt stål – utmaningar och lösningar | ASPAB",
    metaDescription: "Lär dig om utmaningar och lösningar vid laserskärning i rostfritt stål. Vi jämför 304 vs 316, diskuterar kvävgas för oxidfria snitt och visar applikationer.",
    keywords: "laserskarning, rostfritt, stal",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-aluminium",
    title: "Laserskärning i aluminium – teknik, tips och begränsningar",
    excerpt: "Laserskärning i aluminium är en avancerad process som kräver specialkunskap. Läs om reflektivitet, legeringsval, processgas och hur ASPAB skär aluminium med CO2-laser.",
    date: "10 april 2026",
    dateISO: "2026-04-10",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.closeup2,
    ogImage: "/images/og-laserskarning-aluminium_17d5a793.png",
    imageAlt: "Laserskärning av aluminium i ASPAB:s verkstad",
    category: "Kunskap",
    readTime: "7 min",
    metaTitle: "Laserskärning i aluminium – teknik, tips och begränsningar | ASPAB",
    metaDescription: "Lär dig allt om laserskärning i aluminium. Vi utforskar tekniken, utmaningar med reflektivitet, val av legeringar (5754, 6082, 7075) och gasval.",
    keywords: "laserskarning, aluminium",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-hardox-strenx",
    title: "Laserskärning i Hardox och Strenx – höghållfasthetsstål",
    excerpt: "Hardox® och Strenx® är avancerade stål som kräver expertis. Upptäck varför laserskärning är den optimala metoden för att bearbeta dessa material med hög precision och minimal värmepåverkan.",
    date: "11 april 2026",
    dateISO: "2026-04-11",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop4,
    ogImage: "/images/og-laserskarning-hardox-strenx_4434f380.png",
    imageAlt: "Bearbetning av höghållfasthetsstål hos ASPAB",
    category: "Kunskap",
    readTime: "8 min",
    metaTitle: "Laserskärning i Hardox och Strenx – höghållfasthetsstål | ASPAB",
    metaDescription: "Lär dig om laserskärning i Hardox och Strenx. ASPAB erbjuder expertis och precision för bearbetning av höghållfasthetsstål med snäva toleranser.",
    keywords: "laserskarning, hardox, strenx",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-precision-toleranser",
    title: "Toleranser vid laserskärning – vad påverkar precisionen?",
    excerpt: "Lär dig allt om toleranser vid laserskärning. Vi utforskar de 6 nyckelfaktorerna som påverkar precisionen, från materialval till maskinkalibrering, och hur ASPAB säkerställer noggrannhet ner till ±0,1 mm.",
    date: "12 april 2026",
    dateISO: "2026-04-12",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.detail,
    ogImage: "/images/og-laserskarning-precision-toleranser_76253d0f.png",
    imageAlt: "Precisionsdetalj laserskuren med 0.1 mm tolerans",
    category: "Teknik",
    readTime: "7 min",
    metaTitle: "Toleranser vid laserskärning – vad påverkar precisionen? | ASPAB",
    metaDescription: "Hur uppnås hög precision vid laserskärning? Upptäck vilka toleranser som är möjliga och vilka faktorer som påverkar noggrannheten, inklusive material, tjocklek och maskininställningar.",
    keywords: "laserskarning, precision, toleranser",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-kostnader",
    title: "Vad kostar laserskärning? Faktorer som påverkar priset",
    excerpt: "Vad kostar laserskärning? Priset beror på faktorer som material, tjocklek och antal. Denna guide förklarar vad som driver kostnaden och hur du kan optimera din design för en bättre offert.",
    date: "13 april 2026",
    dateISO: "2026-04-13",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.laserOldSite,
    ogImage: "/images/og-laserskarning-kostnader_82431630.png",
    imageAlt: "Produktionslinje för laserskärning hos ASPAB",
    category: "Guide",
    readTime: "7 min",
    metaTitle: "Vad kostar laserskärning? Faktorer som påverkar priset | ASPAB",
    metaDescription: "Undrar du vad laserskärning kostar? Lär dig om de 6 viktigaste faktorerna som påverkar priset: material, tjocklek, komplexitet, antal och efterbehandling. Få tips för en billigare offert.",
    keywords: "laserskarning, kostnader",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-vs-vattenjet",
    title: "Laserskärning vs vattenskärning – en jämförelse",
    excerpt: "Valet mellan laserskärning och vattenskärning är avgörande för ditt projekts framgång. Denna artikel ger en djupgående jämförelse av de två teknikerna, från process och materialpåverkan till precision och kostnad, för att hjälpa dig att fatta rätt beslut.",
    date: "14 april 2026",
    dateISO: "2026-04-14",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.laserCutting,
    ogImage: "/images/og-laserskarning-vs-vattenjet_f59dd87a.png",
    imageAlt: "Jämförelse mellan laserskärning och vattenskärning",
    category: "Teknik",
    readTime: "8 min",
    metaTitle: "Laserskärning vs vattenskärning – en jämförelse | ASPAB",
    metaDescription: "Jämför laserskärning och vattenskärning för att välja rätt metod. Lär dig om process, HAZ, material, precision och kostnad för att optimera din tillverkning.",
    keywords: "laserskarning, vs, vattenjet",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-prototyper",
    title: "Laserskärning för prototyper – från idé till färdig detalj",
    excerpt: "Laserskärning erbjuder en oslagbar kombination av snabbhet, precision och flexibilitet för prototyptillverkning i plåt. Gå från digital ritning till fysisk detalj på rekordtid utan dyra verktygskostnader.",
    date: "15 april 2026",
    dateISO: "2026-04-15",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop3,
    ogImage: "/images/og-laserskarning-prototyper_7c51b261.png",
    imageAlt: "Prototyp i plåt tillverkad med laserskärning",
    category: "Guide",
    readTime: "7 min",
    metaTitle: "Laserskärning för prototyper – från idé till färdig detalj | ASPAB",
    metaDescription: "Lär dig hur laserskärning för prototyper i plåt fungerar. Från DXF-fil till färdig detalj med snabb leverans, hög precision och total designfrihet hos ASPAB.",
    keywords: "laserskarning, prototyper",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-stora-serier",
    title: "Laserskärning i stora serier – effektivitet och volymfördelar",
    excerpt: "Upptäck hur laserskärning i stora serier kan revolutionera din tillverkning. Vi går igenom allt från kostnadsbesparingar och volymfördelar till avancerad nesting och automatiserad produktion hos ASPAB.",
    date: "16 april 2026",
    dateISO: "2026-04-16",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshopOverview,
    ogImage: "/images/og-laserskarning-stora-serier_7bc370be.png",
    imageAlt: "Serieproduktion med laserskärning hos ASPAB",
    category: "Guide",
    readTime: "7 min",
    metaTitle: "Laserskärning i stora serier – effektivitet och volymfördelar | ASPAB",
    metaDescription: "Lär dig om fördelarna med laserskärning i stora serier. Upptäck hur ASPAB:s automatiserade process, nesting och volymfördelar ger kostnadseffektivitet och precision.",
    keywords: "laserskarning, stora, serier",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "kantpressning-guide",
    title: "Kantpressning – en komplett guide till bockning av plåt",
    excerpt: "En komplett guide till kantpressning, en av de mest centrala metoderna inom modern plåtbearbetning. Lär dig om processen, från ritning till färdig detalj, och de tekniska aspekterna som säkerställer ett perfekt resultat.",
    date: "17 april 2026",
    dateISO: "2026-04-17",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop3,
    ogImage: "/images/og-kantpressning-guide_797cb98f.png",
    imageAlt: "Kantpressning av plåt i ASPAB:s verkstad",
    category: "Kunskap",
    readTime: "9 min",
    metaTitle: "Kantpressning – en komplett guide till bockning av plåt | ASPAB",
    metaDescription: "Lär dig allt om kantpressning i vår kompletta guide. Vi täcker processen, beräkning av presskraft, materialbeteende, och DFM-tips för optimal plåtbockning.",
    keywords: "kantpressning, guide",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "kantpressning-toleranser",
    title: "Toleranser vid kantpressning – vad är möjligt?",
    excerpt: "Kantpressning är en process som kräver hög precision. Upptäck vilka faktorer som påverkar noggrannheten och vilka snäva toleranser som är möjliga att uppnå med modern teknik.",
    date: "18 april 2026",
    dateISO: "2026-04-18",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop3,
    ogImage: "/images/og-kantpressning-toleranser_2f492b05.png",
    imageAlt: "Precisionsböjning med kantpress hos ASPAB",
    category: "Teknik",
    readTime: "7 min",
    metaTitle: "Toleranser vid kantpressning – vad är möjligt? | ASPAB",
    metaDescription: "Lär dig om vilka toleranser som är möjliga vid kantpressning. Vi går igenom faktorer som påverkar precision, vinkelnoggrannhet och hur vi på ASPAB uppnår snäva toleranser.",
    keywords: "kantpressning, toleranser",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "kantpressning-material",
    title: "Vilka material kan kantpressas? Stål, rostfritt och aluminium",
    excerpt: "Från segt konstruktionsstål till fjädrande rostfritt och sprickbenäget aluminium, varje metall beter sig unikt i kantpressen. Lär dig hur materialvalet påverkar precision, hållfasthet och slutresultat.",
    date: "19 april 2026",
    dateISO: "2026-04-19",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop1,
    ogImage: "/images/og-kantpressning-material_040321e8.png",
    imageAlt: "Kantpressad plåt i olika material",
    category: "Kunskap",
    readTime: "7 min",
    metaTitle: "Vilka material kan kantpressas? Stål, rostfritt och aluminium | ASPAB",
    metaDescription: "Vilka material kan kantpressas? En djupgående guide till att bocka stål, rostfritt stål, aluminium och höghållfast stål som Hardox. Lär dig om återfjädring och materialval.",
    keywords: "kantpressning, material",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "kantpressning-verktyg",
    title: "Kantpressverktyg – V-öppning, stämplar och matriser",
    excerpt: "Förstå hur valet av kantpressverktyg som stämplar och matriser direkt påverkar kvaliteten och precisionen i bockade plåtdetaljer. Vi utforskar allt från 8x-regeln för V-öppningar till specialverktyg.",
    date: "20 april 2026",
    dateISO: "2026-04-20",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop2,
    ogImage: "/images/og-kantpressning-verktyg_7fdb2267.png",
    imageAlt: "Kantpressverktyg och matriser hos ASPAB",
    category: "Teknik",
    readTime: "7 min",
    metaTitle: "Kantpressverktyg – V-öppning, stämplar och matriser | ASPAB",
    metaDescription: "Lär dig allt om kantpressverktyg. Vi går igenom V-öppning, stämplar, matriser och hur rätt verktygsval säkerställer precision i din plåtbearbetning.",
    keywords: "kantpressning, verktyg",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "kantpressning-konstruktion",
    title: "Konstruera för kantpressning – tips för bättre resultat",
    excerpt: "En genomtänkt konstruktion för kantpressning sparar tid och pengar. Lär dig grunderna i DFM för plåt, från minsta flänslängd till rätt bockradie, för att säkerställa en problemfri tillverkning hos ASPAB.",
    date: "21 april 2026",
    dateISO: "2026-04-21",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.detail,
    ogImage: "/images/og-kantpressning-konstruktion_f740033d.png",
    imageAlt: "Konstruktionsritning för kantpressad detalj",
    category: "Tips",
    readTime: "7 min",
    metaTitle: "Konstruera för kantpressning – tips för bättre resultat | ASPAB",
    metaDescription: "Lär dig konstruera för kantpressning med våra DFM-tips. Undvik vanliga misstag, optimera din design för bockning och säkerställ ett effektivt resultat.",
    keywords: "kantpressning, konstruktion",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "kantpressning-vs-rullformning",
    title: "Kantpressning vs rullformning – när passar vad?",
    excerpt: "Kantpressning och rullformning är två metoder för att forma plåt, men de passar för helt olika ändamål. Vi reder ut skillnaderna i flexibilitet, kostnad och volym för att hjälpa dig välja rätt.",
    date: "22 april 2026",
    dateISO: "2026-04-22",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop4,
    ogImage: "/images/og-kantpressning-vs-rullformning_dd41b014.png",
    imageAlt: "Jämförelse kantpressning och rullformning",
    category: "Teknik",
    readTime: "7 min",
    metaTitle: "Kantpressning vs rullformning – när passar vad? | ASPAB",
    metaDescription: "En djupgående jämförelse mellan kantpressning och rullformning. Lär dig om flexibilitet, kostnader, volymer och när du ska välja respektive metod.",
    keywords: "kantpressning, vs, rullformning",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "svetsning-metoder-industri",
    title: "Svetsmetoder inom industrin – MIG, MAG, TIG och mer",
    excerpt: "Utforska de vanligaste svetsmetoderna inom industrin, från den mångsidiga MIG/MAG-svetsen till precisionen i TIG. Förstå när varje metod används för att optimera kvalitet och effektivitet.",
    date: "23 april 2026",
    dateISO: "2026-04-23",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.svetsningOldSite,
    ogImage: "/images/og-svetsning-metoder-industri_d14e3f58.png",
    imageAlt: "Industriell svetsning hos ASPAB",
    category: "Kunskap",
    readTime: "9 min",
    metaTitle: "Svetsmetoder inom industrin – MIG, MAG, TIG och mer | ASPAB",
    metaDescription: "En komplett guide till industriella svetsmetoder. Lär dig om fördelar och nackdelar med MIG/MAG, TIG, MMA och robotsvetsning för stål, rostfritt & aluminium.",
    keywords: "svetsning, metoder, industri",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "svetsning-rostfritt",
    title: "Svetsning av rostfritt stål – teknik och utmaningar",
    excerpt: "Att svetsa i rostfritt stål kräver precision och kunskap för att hantera utmaningar som värmetillförsel och deformation. TIG-svetsning är ofta den bästa metoden för att uppnå en stark, hållbar och korrosionsbeständig fog.",
    date: "24 april 2026",
    dateISO: "2026-04-24",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop2,
    ogImage: "/images/og-svetsning-rostfritt_3dafaf60.png",
    imageAlt: "TIG-svetsning av rostfritt stål",
    category: "Kunskap",
    readTime: "7 min",
    metaTitle: "Svetsning av rostfritt stål – teknik och utmaningar | ASPAB",
    metaDescription: "Lär dig om utmaningarna och teknikerna för svetsning av rostfritt stål. Vi täcker TIG-svetsning, värmetillförsel, rotgas och efterbehandling.",
    keywords: "svetsning, rostfritt",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "svetsning-aluminium",
    title: "Svetsning av aluminium – varför det kräver specialkompetens",
    excerpt: "Aluminium är notoriskt utmanande att svetsa på grund av dess unika egenskaper. Det kräver specialiserad kunskap för att hantera oxidskikt, hög värmeledning och risk för porositet för att uppnå en stark och hållbar fog.",
    date: "25 april 2026",
    dateISO: "2026-04-25",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.closeup2,
    ogImage: "/images/og-svetsning-aluminium_e97d4e64.png",
    imageAlt: "Svetsning av aluminium med TIG-metod",
    category: "Kunskap",
    readTime: "7 min",
    metaTitle: "Svetsning av aluminium – varför det kräver specialkompetens | ASPAB",
    metaDescription: "Lär dig varför svetsning av aluminium är så krävande. Vi går igenom utmaningar som oxidskikt, värmeledning och porositet, samt tekniker som AC TIG.",
    keywords: "svetsning, aluminium",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "svetsning-kvalitetskontroll",
    title: "Kvalitetskontroll vid svetsning – metoder och standarder",
    excerpt: "En grundlig genomgång av metoder och standarder för kvalitetskontroll vid svetsning. Upptäck vikten av oförstörande provning (NDT) och hur standarder som ISO 3834 säkerställer hållbara och säkra konstruktioner.",
    date: "26 april 2026",
    dateISO: "2026-04-26",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshopOverview,
    ogImage: "/images/og-svetsning-kvalitetskontroll_91539428.png",
    imageAlt: "Kvalitetskontroll av svetsfog",
    category: "Teknik",
    readTime: "8 min",
    metaTitle: "Kvalitetskontroll vid svetsning – metoder och standarder | ASPAB",
    metaDescription: "Lär dig allt om kvalitetskontroll vid svetsning. Vi går igenom NDT-metoder som RT, UT, MT & PT, samt viktiga standarder som ISO 3834 och EN 1090.",
    keywords: "svetsning, kvalitetskontroll",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "svetsning-konstruktion",
    title: "Konstruera för svetsning – design som förenklar tillverkning",
    excerpt: "En robust svetsad konstruktion börjar vid ritbordet. Genom att designa med svetsning i åtanke (DFM) kan du minska kostnader, korta ledtider och säkerställa en slutprodukt av högsta kvalitet.",
    date: "27 april 2026",
    dateISO: "2026-04-27",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop1,
    ogImage: "/images/og-svetsning-konstruktion_2a27e47e.png",
    imageAlt: "Svetskonstruktion och fogtyper",
    category: "Tips",
    readTime: "7 min",
    metaTitle: "Konstruera för svetsning – design som förenklar tillverkning | ASPAB",
    metaDescription: "Lär dig konstruera för svetsning (DFM). Vår guide täcker fogtyper, åtkomlighet, deformationskontroll och materialval för en effektiv och hållbar design.",
    keywords: "svetsning, konstruktion",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "stalsorter-guide",
    title: "Guide till stålsorter – S235, S355, Domex, Hardox och fler",
    excerpt: "En djupgående guide till de vanligaste och mest högpresterande stålsorterna. Lär dig skillnaden mellan konstruktionsstål, höghållfast stål och slitstål för att göra rätt materialval för ditt projekt.",
    date: "28 april 2026",
    dateISO: "2026-04-28",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.closeup1,
    ogImage: "/images/og-stalsorter-guide_9a1c6a46.png",
    imageAlt: "Olika stålsorter för laserskärning",
    category: "Kunskap",
    readTime: "9 min",
    metaTitle: "Guide till stålsorter – S235, S355, Domex, Hardox och fler | ASPAB",
    metaDescription: "En komplett guide till stålsorter som S235, S355, Strenx och Hardox. Lär dig om deras egenskaper, användningsområden och hur du väljer rätt stål.",
    keywords: "stalsorter, guide",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "rostfritt-304-vs-316",
    title: "Rostfritt 304 vs 316 – skillnader och användningsområden",
    excerpt: "Valet mellan rostfritt stål 304 och 316 är avgörande för din produkts livslängd och prestanda. Lär dig de kemiska och praktiska skillnaderna, från korrosionsbeständighet till kostnad, för att göra ett informerat materialval.",
    date: "29 april 2026",
    dateISO: "2026-04-29",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.closeup2,
    ogImage: "/images/og-rostfritt-304-vs-316_b59e6247.png",
    imageAlt: "Rostfritt stål 304 och 316 i jämförelse",
    category: "Kunskap",
    readTime: "7 min",
    metaTitle: "Rostfritt 304 vs 316 – skillnader och användningsområden | ASPAB",
    metaDescription: "En djupgående jämförelse mellan rostfritt stål 304 (1.4301) och 316 (1.4404). Upptäck skillnader i kemisk sammansättning, korrosionsskydd, kostnad och applikationer.",
    keywords: "rostfritt, 304, vs, 316",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "aluminium-legeringar",
    title: "Aluminiumlegeringar för industrin – 5000, 6000 och 7000-serien",
    excerpt: "Allt aluminium är inte skapat lika. Vi reder ut skillnaderna mellan de vanligaste legeringsserierna och hjälper dig välja rätt material för ditt projekt.",
    date: "30 april 2026",
    dateISO: "2026-04-30",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.laserCutting,
    ogImage: "/images/og-aluminium-legeringar_29f6e45f.png",
    imageAlt: "Aluminiumlegeringar för industriell användning",
    category: "Kunskap",
    readTime: "8 min",
    metaTitle: "Aluminiumlegeringar för industrin – 5000, 6000 och 7000-serien | ASPAB",
    metaDescription: "En guide till aluminiumlegeringar för industrin. Lär dig skillnaderna mellan 5000-, 6000- och 7000-serien, deras egenskaper och typiska användningsområden.",
    keywords: "aluminium, legeringar",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "ytbehandling-plat",
    title: "Ytbehandling av plåt – galvanisering, pulverlackering och mer",
    excerpt: "Ytbehandling skyddar mot korrosion och ger en estetisk finish. Lär dig om varmförzinkning, pulverlackering och andra metoder för att välja rätt skydd för din produkt.",
    date: "1 maj 2026",
    dateISO: "2026-05-01",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.lissmacMaskin,
    ogImage: "/images/og-ytbehandling-plat_74804581.png",
    imageAlt: "Ytbehandlad plåtdetalj",
    category: "Kunskap",
    readTime: "8 min",
    metaTitle: "Ytbehandling av plåt – galvanisering, pulverlackering och mer | ASPAB",
    metaDescription: "En komplett guide till ytbehandling av plåt. Jämför varmförzinkning, pulverlackering, elförzinkning och anodisering för att välja rätt skydd och finish.",
    keywords: "ytbehandling, plat",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "korrosionsskydd-metall",
    title: "Korrosionsskydd för metallkonstruktioner – metoder och val",
    excerpt: "Förstå hur du väljer rätt korrosionsskydd för dina metallkonstruktioner. Vi går igenom allt från korrosivitetsklasser enligt ISO 12944 till de mest effektiva metoderna för ytbehandling och design.",
    date: "2 maj 2026",
    dateISO: "2026-05-02",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop2,
    ogImage: "/images/og-korrosionsskydd-metall_d0b5e005.png",
    imageAlt: "Korrosionsskyddad metallkonstruktion",
    category: "Kunskap",
    readTime: "8 min",
    metaTitle: "Korrosionsskydd för metallkonstruktioner – metoder och val | ASPAB",
    metaDescription: "En komplett guide till korrosionsskydd för metall. Lär dig om korrosivitetsklasser (C1-C5), metoder som varmförzinkning & design för att skydda dina konstruktioner.",
    keywords: "korrosionsskydd, metall",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "materialval-guide",
    title: "Så väljer du rätt material för ditt projekt: En komplett guide",
    excerpt: "Lär dig att navigera i materialdjungeln med vår kompletta guide. Vi går igenom 7 nyckelfaktorer, från hållfasthet till kostnad, och hjälper dig att välja rätt stål, rostfritt eller aluminium för just ditt projekt.",
    date: "3 maj 2026",
    dateISO: "2026-05-03",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.detail,
    ogImage: "/images/og-materialval-guide_1838d3ee.png",
    imageAlt: "Materialval för plåtbearbetning",
    category: "Guide",
    readTime: "8 min",
    metaTitle: "Så väljer du rätt material för ditt projekt: En komplett guide | ASPAB",
    metaDescription: "En komplett guide för att välja rätt material. Jämför stål, rostfritt stål och aluminium baserat på hållfasthet, korrosion, vikt, kostnad och bearbetbarhet.",
    keywords: "materialval, guide",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "hardox-slitplat",
    title: "Hardox slitplåt – egenskaper, användning och bearbetning",
    excerpt: "Hardox® slitplåt är en global ledare inom nötningsbeständigt stål, känt för sin exceptionella hårdhet och seghet. Upptäck dess egenskaper och hur ASPAB bearbetar detta avancerade material.",
    date: "4 maj 2026",
    dateISO: "2026-05-04",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop4,
    ogImage: "/images/og-hardox-slitplat_196c7d73.png",
    imageAlt: "Hardox slitplåt bearbetad hos ASPAB",
    category: "Kunskap",
    readTime: "8 min",
    metaTitle: "Hardox slitplåt – egenskaper, användning och bearbetning | ASPAB",
    metaDescription: "Utforska Hardox slitplåt (400, 450, 500) – dess unika hårdhet, seghet och användningsområden. Lär dig om skärning, svetsning och bearbetning hos ASPAB.",
    keywords: "hardox, slitplat",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "fran-ritning-till-fardig-detalj",
    title: "Från ritning till färdig detalj – så fungerar tillverkningsprocessen",
    excerpt: "Nyfiken på hur en bit stål blir en komplex komponent? Vi bryter ner hela tillverkningsprocessen, från första ritning till färdiglevererad detalj, och visar hur modern plåtbearbetning fungerar i praktiken.",
    date: "5 maj 2026",
    dateISO: "2026-05-05",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop3,
    ogImage: "/images/og-fran-ritning-till-fardig-detalj_3990531f.png",
    imageAlt: "Tillverkningsprocessen från ritning till färdig detalj",
    category: "Guide",
    readTime: "8 min",
    metaTitle: "Från ritning till färdig detalj – så fungerar tillverkningsprocessen | ASPAB",
    metaDescription: "Följ med på resan från digital CAD-ritning till färdig metalldetalj. Vi förklarar hela tillverkningsprocessen, från offert och nestning till laserskärning och svetsning.",
    keywords: "fran, ritning, till, fardig, detalj",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "iso-certifiering-metallbearbetning",
    title: "ISO-certifiering inom metallbearbetning – 9001, 14001, 45001",
    excerpt: "Förstå varför ISO-certifieringar som 9001, 14001 och 45001 är avgörande för kvalitet och pålitlighet inom metallbearbetning. En guide till de viktigaste standarderna i branschen.",
    date: "6 maj 2026",
    dateISO: "2026-05-06",
    lastUpdated: "23 september 2026",
    lastUpdatedISO: "2026-09-23",
    image: IMAGES.team1,
    ogImage: "/images/og-iso-certifiering-metallbearbetning_771cb49b.png",
    imageAlt: "ASPAB:s team arbetar med kvalitetssystem",
    category: "Kunskap",
    readTime: "8 min",
    metaTitle: "ISO-certifiering inom metallbearbetning – 9001, 14001, 45001 | ASPAB",
    metaDescription: "Lär dig allt om ISO-certifiering inom metallbearbetning. Vi förklarar ISO 9001 (kvalitet), 14001 (miljö) & 45001 (arbetsmiljö) och deras betydelse för dig som kund.",
    keywords: "iso, certifiering, metallbearbetning",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "avgradning-ytfinish",
    title: "Avgradning och ytfinish – varför det spelar roll",
    excerpt: "Ett ofta förbisett men kritiskt steg i modern tillverkning är avgradning. Det handlar inte bara om estetik, utan om säkerhet, passform och hållbarhet för slutprodukten.",
    date: "7 maj 2026",
    dateISO: "2026-05-07",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.lissmacMaskin,
    ogImage: "/images/og-avgradning-ytfinish_49b550aa.png",
    imageAlt: "Lissmac gradningsmaskin hos ASPAB",
    category: "Kunskap",
    readTime: "7 min",
    metaTitle: "Avgradning och ytfinish – varför det spelar roll | ASPAB",
    metaDescription: "Lär dig varför avgradning och ytfinish är kritiska steg i plåtbearbetning. Upptäck hur ASPAB säkerställer kvalitet, säkerhet och hållbarhet med avancerad teknik.",
    keywords: "avgradning, ytfinish",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "forsta-bit-kontroll",
    title: "Första-bit-kontroll – kvalitetssäkring i produktion",
    excerpt: "Första-bit-kontroll (FAI) är en kritisk kvalitetssäkringsprocess som verifierar att den första komponenten i en serie uppfyller alla specifikationer. Lär dig hur denna metod, med hjälp av avancerad mätteknik, förhindrar kostsamma produktionsfel.",
    date: "8 maj 2026",
    dateISO: "2026-05-08",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshopOverview,
    ogImage: "/images/og-forsta-bit-kontroll_f364380a.png",
    imageAlt: "Mätkontroll av första bit",
    category: "Teknik",
    readTime: "7 min",
    metaTitle: "Första-bit-kontroll – kvalitetssäkring i produktion | ASPAB",
    metaDescription: "En djupgående guide till första-bit-kontroll (FAI) i tillverkning. Förstå syftet, processen, mätverktygen och hur det säkerställer kvaliteten hos ASPAB.",
    keywords: "forsta, bit, kontroll",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "nestning-materialoptimering",
    title: "Nestning och materialoptimering – minska spill vid laserskärning",
    excerpt: "Genom avancerad nestning omvandlar vi en komplex uppgift till en vetenskap som ökar materialutnyttjandet från 60-70% till över 95%, vilket minskar kostnader och miljöpåverkan.",
    date: "9 maj 2026",
    dateISO: "2026-05-09",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.heroLaser,
    ogImage: "/images/og-nestning-materialoptimering_1f729469.png",
    imageAlt: "Optimerad nestning vid laserskärning",
    category: "Teknik",
    readTime: "7 min",
    metaTitle: "Nestning och materialoptimering – minska spill vid laserskärning | ASPAB",
    metaDescription: "Lär dig hur avancerad nestning och materialoptimering kan minska spillet vid laserskärning från 40% till under 5%. ASPAB förklarar tekniken som sänker kostnader.",
    keywords: "nestning, materialoptimering",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "ledtider-leveranstider",
    title: "Ledtider inom plåtbearbetning – vad påverkar leveranstiden?",
    excerpt: "Förstå de avgörande faktorerna som påverkar ledtider inom plåtbearbetning, från materialval och designkomplexitet till produktionskapacitet. Lär dig hur du kan optimera din beställning för att säkerställa snabbast möjliga leverans från ASPAB.",
    date: "10 maj 2026",
    dateISO: "2026-05-10",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.snabbLeverans,
    ogImage: "/images/og-ledtider-leveranstider_9550fff0.png",
    imageAlt: "Snabb leverans av laserskurna detaljer",
    category: "Guide",
    readTime: "7 min",
    metaTitle: "Ledtider inom plåtbearbetning – vad påverkar leveranstiden? | ASPAB",
    metaDescription: "Vad påverkar ledtider och leveranstider för laserskärning och plåtbearbetning? Läs om de 5 nyckelfaktorerna och få tips på hur du får snabbare leverans.",
    keywords: "ledtider, leveranstider",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-fordonsindustrin",
    title: "Laserskärning inom fordonsindustrin – krav och tillämpningar",
    excerpt: "Från chassidetaljer i höghållfast stål till komplexa prototyper – laserskärning är en grundpelare i modern fordonstillverkning. Upptäck de tuffa kraven på precision, materialkunskap och kvalitet som definierar branschen och hur ASPAB möter dem.",
    date: "11 maj 2026",
    dateISO: "2026-05-11",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.referensVolvo,
    ogImage: "/images/og-laserskarning-fordonsindustrin_91a36a0e.png",
    imageAlt: "Fordonskomponenter tillverkade av ASPAB",
    category: "Bransch",
    readTime: "8 min",
    metaTitle: "Laserskärning inom fordonsindustrin – krav och tillämpningar | ASPAB",
    metaDescription: "Laserskärning är en kritisk process för fordonsindustrin. Lär dig om kraven, tillämpningarna och hur ASPAB levererar komponenter till ledare som Volvo.",
    keywords: "laserskarning, fordonsindustrin",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-forsvarsindustrin",
    title: "Plåtbearbetning för försvarsindustrin – säkerhet och precision",
    excerpt: "Försvarsindustrin ställer absoluta krav på precision, säkerhet och spårbarhet. Utforska de unika utmaningar och avancerade lösningar som krävs för plåtbearbetning på denna kritiska marknad, från materialspårbarhet till hantering av pansarstål.",
    date: "12 maj 2026",
    dateISO: "2026-05-12",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop4,
    ogImage: "/images/og-laserskarning-forsvarsindustrin_52be9519.png",
    imageAlt: "Försvarskomponenter i höghållfasthetsstål",
    category: "Bransch",
    readTime: "8 min",
    metaTitle: "Plåtbearbetning för försvarsindustrin – säkerhet och precision | ASPAB",
    metaDescription: "En djupdykning i plåtbearbetning för försvarsindustrin. Lär dig om kraven på materialspårbarhet, Hardox, snäva toleranser och säkerhet. ASPAB är er partner.",
    keywords: "laserskarning, forsvarsindustrin",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-byggindustrin",
    title: "Laserskärning inom byggindustrin – stålkonstruktioner och fasader",
    excerpt: "Laserskärning är en hörnsten i modern byggindustri, avgörande för allt från bärande stålkonstruktioner till komplexa arkitektoniska fasader. Tekniken möjliggör oöverträffad precision och designfrihet, vilket är essentiellt för att möta dagens höga krav på säkerhet, hållbarhet och estetik.",
    date: "13 maj 2026",
    dateISO: "2026-05-13",
    lastUpdated: "23 september 2026",
    lastUpdatedISO: "2026-09-23",
    image: IMAGES.workshop1,
    ogImage: "/images/og-laserskarning-byggindustrin_dd7c98c4.png",
    imageAlt: "Stålkonstruktioner för byggindustrin",
    category: "Bransch",
    readTime: "7 min",
    metaTitle: "Laserskärning inom byggindustrin – stålkonstruktioner och fasader | ASPAB",
    metaDescription: "Utforska hur laserskärning revolutionerar byggindustrin. Lär dig om dess roll i stålkonstruktioner, fasadplåt och vikten av EN 1090-certifiering.",
    keywords: "laserskarning, byggindustrin",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-jordbruk",
    title: "Plåtbearbetning för jordbruksmaskiner – slitage och hållbarhet",
    excerpt: "I det moderna jordbruket ställs extrema krav på maskinparken. Avancerad plåtbearbetning med rätt materialval är avgörande för att maximera livslängd och prestanda.",
    date: "14 maj 2026",
    dateISO: "2026-05-14",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop2,
    ogImage: "/images/og-laserskarning-jordbruk_787d8c68.png",
    imageAlt: "Slitdelar för jordbruksmaskiner",
    category: "Bransch",
    readTime: "7 min",
    metaTitle: "Plåtbearbetning för jordbruksmaskiner – slitage och hållbarhet | ASPAB",
    metaDescription: "Lär dig hur avancerad plåtbearbetning med Hardox och Strenx maximerar livslängden på jordbruksmaskiner. ASPAB levererar slitdelar och reparationsdelar.",
    keywords: "laserskarning, jordbruk",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-energisektorn",
    title: "Metallbearbetning för energisektorn – vind, sol och vätgas",
    excerpt: "För att bygga framtidens fossilfria energisystem krävs avancerad metallbearbetning. Vi utforskar hur laserskärning, kantpressning och svetsning av specialstål som Strenx och rostfritt 316L är avgörande för att tillverka komponenter med hög precision för vindkraft, solenergi och vätgasinfrastruktur.",
    date: "15 maj 2026",
    dateISO: "2026-05-15",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshopOverview,
    ogImage: "/images/og-laserskarning-energisektorn_589fb489.png",
    imageAlt: "Metallkomponenter för energisektorn",
    category: "Bransch",
    readTime: "8 min",
    metaTitle: "Metallbearbetning för energisektorn – vind, sol och vätgas | ASPAB",
    metaDescription: "Lär dig hur avancerad metallbearbetning och materialval är kritiska för energisektorn. ASPAB levererar komponenter för vindkraft, solenergi och vätgas.",
    keywords: "laserskarning, energisektorn",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-livsmedel",
    title: "Laserskärning för livsmedelsindustrin – hygien och rostfritt",
    excerpt: "Upptäck varför modern laserskärning av rostfritt stål är avgörande för att möta livsmedelsindustrins strikta krav på hygienisk design, ytkvalitet och säkerhet. Vi utforskar materialval, EHEDG-riktlinjer och tillverkningsmetoder som garanterar säker och pålitlig utrustning.",
    date: "16 maj 2026",
    dateISO: "2026-05-16",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.closeup2,
    ogImage: "/images/og-laserskarning-livsmedel_15e013f5.png",
    imageAlt: "Rostfri utrustning för livsmedelsindustrin",
    category: "Bransch",
    readTime: "7 min",
    metaTitle: "Laserskärning för livsmedelsindustrin – hygien och rostfritt | ASPAB",
    metaDescription: "Laserskärning i rostfritt stål (304, 316L) är avgörande för livsmedelsindustrin. Lär dig om hygienisk design, EHEDG, och ytkrav (Ra ≤ 0.8µm) för säker utrustning.",
    keywords: "laserskarning, livsmedel",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-marin",
    title: "Plåtbearbetning för marin och offshore – korrosion och hållfasthet",
    excerpt: "Den marina och offshore-industrin ställer extrema krav på material och tillverkning för att motstå korrosion och mekanisk påfrestning. Lär dig om materialval som 316L, duplex och aluminium, samt hur avancerad plåtbearbetning garanterar säkerhet och hållbarhet.",
    date: "17 maj 2026",
    dateISO: "2026-05-17",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshop4,
    ogImage: "/images/og-laserskarning-marin_b4846489.png",
    imageAlt: "Marin plåtbearbetning i korrosionsbeständigt material",
    category: "Bransch",
    readTime: "7 min",
    metaTitle: "Plåtbearbetning för marin och offshore – korrosion och hållfasthet | ASPAB",
    metaDescription: "En djupgående guide till plåtbearbetning för marin och offshore. Upptäck kritiska materialval, korrosionsutmaningar och avancerade tillverkningsmetoder.",
    keywords: "laserskarning, marin",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "laserskarning-moblering-design",
    title: "Laserskärning inom möbler och design – kreativa tillämpningar",
    excerpt: "Utforska hur laserskärning förvandlar designvärlden, från skräddarsydda möbler i metall och dekorativa arkitektoniska paneler till unika konstinstallationer. Upptäck de kreativa möjligheterna med precisionsteknik hos ASPAB.",
    date: "18 maj 2026",
    dateISO: "2026-05-18",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.closeup1,
    ogImage: "/images/og-laserskarning-moblering-design_d60305af.png",
    imageAlt: "Laserskurna designdetaljer i metall",
    category: "Bransch",
    readTime: "7 min",
    metaTitle: "Laserskärning inom möbler och design – kreativa tillämpningar | ASPAB",
    metaDescription: "Upptäck kreativa tillämpningar för laserskärning inom möbler och design. Lär dig om dekorativ plåt, arkitektoniska paneler och hur ASPAB förverkligar visioner.",
    keywords: "laserskarning, moblering, design",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "automation-metallbearbetning",
    title: "Automation inom metallbearbetning – robotar och Industry 4.0",
    excerpt: "Utforska hur automation, robotteknik och Industry 4.0 omformar metallbearbetningsindustrin. Upptäck fördelarna med smarta fabriker, från automatiserad materialhantering och robotsvetsning till digitala tvillingar och AI-driven optimering.",
    date: "19 maj 2026",
    dateISO: "2026-05-19",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshopOverview,
    ogImage: "/images/og-automation-metallbearbetning_6d953075.png",
    imageAlt: "Automatiserad metallbearbetning hos ASPAB",
    category: "Framtid",
    readTime: "8 min",
    metaTitle: "Automation inom metallbearbetning – robotar och Industry 4.0 | ASPAB",
    metaDescription: "Lär dig allt om automation inom metallbearbetning. Vi täcker Industry 4.0, robotsvetsning, IoT, digitala tvillingar och hur det skapar effektivare och säkrare produktion.",
    keywords: "automation, metallbearbetning",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "hallbar-tillverkning-plat",
    title: "Hållbar tillverkning – miljöarbete inom plåtbearbetning",
    excerpt: "I en tid där industrins miljöpåverkan granskas noggrant, står hållbarhet inte längre som ett val, utan som en nödvändighet. För svensk tillverkningsindustri, och i synnerhet inom plåtbearbetning, innebär detta en omställning mot grönare processer och ett cirkulärt tänkande.",
    date: "20 maj 2026",
    dateISO: "2026-05-20",
    lastUpdated: "23 september 2026",
    lastUpdatedISO: "2026-09-23",
    image: IMAGES.workshop1,
    ogImage: "/images/og-hallbar-tillverkning-plat_57729cad.png",
    imageAlt: "Hållbar tillverkning och materialåtervinning",
    category: "Framtid",
    readTime: "8 min",
    metaTitle: "Hållbar tillverkning – miljöarbete inom plåtbearbetning | ASPAB",
    metaDescription: "Lär dig om hållbar tillverkning inom plåtbearbetning. Vi går igenom materialåtervinning, energieffektiv produktion, resurshushållning och ISO 14001.",
    keywords: "hallbar, tillverkning, plat",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "digitalisering-platindustrin",
    title: "Digitalisering i plåtindustrin – från CAD till smart fabrik",
    excerpt: "Digitaliseringen transformerar plåtindustrin från pappersritningar till en sömlös, datadriven process. Upptäck hur ASPAB använder CAD/CAM, MES-system och papperslös produktion för att leverera högre precision och full spårbarhet.",
    date: "21 maj 2026",
    dateISO: "2026-05-21",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.laserCutting,
    ogImage: "/images/og-digitalisering-platindustrin_827f0f8e.png",
    imageAlt: "Digital styrning av laserskärmaskin",
    category: "Framtid",
    readTime: "8 min",
    metaTitle: "Digitalisering i plåtindustrin – från CAD till smart fabrik | ASPAB",
    metaDescription: "Utforska digitalisering i plåtindustrin. Lär dig om CAD/CAM, smarta fabriker, MES-system och hur ASPAB leder utvecklingen för ökad effektivitet och kvalitet.",
    keywords: "digitalisering, platindustrin",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "framtidens-laserteknik",
    title: "Framtidens laserteknik – trender och innovationer",
    excerpt: "Lasertekniken accelererar med innovationer som omdefinierar gränserna för metallbearbetning. Utforska framtidens trender, från högheffektlasrar och AI-styrd precision till nya våglängder för avancerade material.",
    date: "22 maj 2026",
    dateISO: "2026-05-22",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.heroLaser,
    ogImage: "/images/og-framtidens-laserteknik_5cb9f43f.png",
    imageAlt: "Framtidens laserteknik och innovationer",
    category: "Framtid",
    readTime: "8 min",
    metaTitle: "Framtidens laserteknik – trender och innovationer | ASPAB",
    metaDescription: "Utforska framtidens laserteknik med ASPAB. Läs om trender som högheffektlasrar (20kW+), gröna lasrar för koppar, AI-optimering och 3D-laserskärning.",
    keywords: "framtidens, laserteknik",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "valja-underleverantor-plat",
    title: "Så väljer du rätt underleverantör för plåtbearbetning",
    excerpt: "Att välja rätt partner för plåtbearbetning är avgörande för din framgång. Denna guide ger dig en komplett checklista och de rätta frågorna att ställa för att hitta en pålitlig och långsiktig underleverantör.",
    date: "23 maj 2026",
    dateISO: "2026-05-23",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.team2,
    ogImage: "/images/og-valja-underleverantor-plat_9ee15149.png",
    imageAlt: "ASPAB:s team diskuterar kundprojekt",
    category: "Guide",
    readTime: "8 min",
    metaTitle: "Så väljer du rätt underleverantör för plåtbearbetning | ASPAB",
    metaDescription: "Lär dig hur du väljer rätt underleverantör för plåtbearbetning. Vår guide täcker allt från teknisk kapacitet och kvalitetssystem till en checklista för din utvärdering.",
    keywords: "valja, underleverantor, plat",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "offertforfragan-guide",
    title: "Så gör du en bra offertförfrågan för laserskärning",
    excerpt: "En välformulerad offertförfrågan är nyckeln till en snabb och exakt prisbild för laserskärning. Lär dig vilken information du behöver inkludera för att få bästa möjliga service från ASPAB.",
    date: "24 maj 2026",
    dateISO: "2026-05-24",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.team1,
    ogImage: "/images/og-offertforfragan-guide_b63591b0.png",
    imageAlt: "Offertförfrågan för laserskärning",
    category: "Guide",
    readTime: "7 min",
    metaTitle: "Så gör du en bra offertförfrågan för laserskärning | ASPAB",
    metaDescription: "Lär dig skriva en perfekt offertförfrågan för laserskärning. Vår guide täcker material, filformat (DXF/STEP) & vanliga misstag för snabbare & bättre priser.",
    keywords: "offertforfragan, guide",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "dxf-filformat-guide",
    title: "DXF-filer för laserskärning – komplett guide till filformat",
    excerpt: "Förstå varför en korrekt DXF-fil är avgörande för laserskärning. Vår guide täcker allt från STEP-filer och vanliga ritningsfel till hur du optimerar din design för en effektiv tillverkningsprocess hos ASPAB.",
    date: "25 maj 2026",
    dateISO: "2026-05-25",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.detail,
    ogImage: "/images/og-dxf-filformat-guide_5dcc3be8.png",
    imageAlt: "DXF-fil öppnad i CAD-program",
    category: "Guide",
    readTime: "8 min",
    metaTitle: "DXF-filer för laserskärning – komplett guide till filformat | ASPAB",
    metaDescription: "En komplett guide till DXF-filer för laserskärning. Lär dig om STEP, DWG, vanliga fel och hur du skapar perfekta ritningsfiler för tillverkning.",
    keywords: "dxf, filformat, guide",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },
  {
    slug: "plattillverkning-ordlista",
    title: "Ordlista för plåttillverkning – 100+ termer förklarade",
    excerpt: "Förstå språket inom plåtbearbetning med vår omfattande ordlista. Vi förklarar över 100 viktiga termer inom laserskärning, kantpressning och svetsning.",
    date: "26 maj 2026",
    dateISO: "2026-05-26",
    lastUpdated: "18 september 2026",
    lastUpdatedISO: "2026-09-18",
    image: IMAGES.workshopOverview,
    ogImage: "/images/og-plattillverkning-ordlista_57dfcb16.png",
    imageAlt: "Plåtbearbetning terminologi och ordlista",
    category: "Kunskap",
    readTime: "10 min",
    metaTitle: "Ordlista för plåttillverkning – 100+ termer förklarade | ASPAB",
    metaDescription: "Söker du en komplett ordlista för plåttillverkning? Lär dig 100+ termer inom laserskärning, kantpressning & svetsning. Din guide från ASPAB.",
    keywords: "plattillverkning, ordlista",
    author: {
      name: "Robin Fischer",
      role: "VD, ASPAB",
      description: "Robin Fischer har varit ägare och VD för ASPAB sedan augusti 2020 och arbetar med laserskärning, kantpressning, svetsning och metallbearbetning.",
    },
    factCheckedBy: {
      name: "ASPAB Produktionsteam",
      role: "Produktionsledning",
      description: "Tekniskt innehåll granskat och verifierat av ASPAB:s produktionsledning.",
    },
    keyTakeaways: [
      "Laserskärning med toleranser ner till 0,1 mm.",
      "Material upp till 25 mm svart stål och 20 mm rostfritt.",
      "Snabba leveranser från Eskilstuna sedan 1993.",
    ],
  },

];

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

export function getArticlesByCategory(category: string): BlogPost[] {
  return blogPosts.filter((p) => p.category === category);
}

export function getArticlesByCategories(categories: string[]): BlogPost[] {
  return blogPosts.filter((p) => categories.includes(p.category));
}
